// src/components/FeedbackButton.js
//
// Persistent floating feedback button (bottom-right) visible on every
// authenticated page. Opens a modal with:
//   - Free-text message (required)
//   - Optional 1-5 star rating
// On submit, captures route, tournament_id, pool_id, and sends to
// POST /feedback. A confirmation message is shown on success.

import React, { useState } from 'react';
import { submitFeedback } from '../services/api';

function StarRating({ value, onChange }) {
    const [hovered, setHovered] = useState(null);
    return (
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(star => (
                <button
                    key={star}
                    type="button"
                    onClick={() => onChange(value === star ? null : star)}
                    onMouseEnter={() => setHovered(star)}
                    onMouseLeave={() => setHovered(null)}
                    className="text-2xl transition-colors focus:outline-none"
                >
                    <span className={(hovered !== null ? star <= hovered : star <= (value || 0))
                        ? 'text-orange-400'
                        : 'text-gray-600'}>
                        ★
                    </span>
                </button>
            ))}
        </div>
    );
}

function FeedbackButton({ page }) {
    const [open,    setOpen]    = useState(false);
    const [message, setMessage] = useState('');
    const [rating,  setRating]  = useState(null);
    const [saving,  setSaving]  = useState(false);
    const [done,    setDone]    = useState(false);
    const [error,   setError]   = useState('');

    const handleOpen = () => {
        setOpen(true);
        setDone(false);
        setError('');
        setMessage('');
        setRating(null);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubmit = async () => {
        if (!message.trim()) {
            setError('Please enter a message before submitting.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await submitFeedback({
                message:       message.trim(),
                rating:        rating || undefined,
                route:         page || '/',
            });
            setDone(true);
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {/* Floating trigger button */}
            <button
                onClick={handleOpen}
                className="fixed bottom-6 right-6 z-40 bg-orange-500 hover:bg-orange-400 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg transition-colors flex items-center gap-2"
                aria-label="Give feedback"
            >
                <span>💬</span>
                <span className="hidden sm:inline">Feedback</span>
            </button>

            {/* Modal overlay */}
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                    <div
                        className="absolute inset-0 bg-black/60"
                        onClick={handleClose}
                    />
                    <div className="relative bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6 shadow-2xl">

                        {done ? (
                            /* Success state */
                            <div className="text-center py-4">
                                <div className="text-4xl mb-3">🎉</div>
                                <p className="text-white font-bold text-lg mb-2">Thanks for the feedback!</p>
                                <p className="text-gray-400 text-sm mb-6">
                                    A summary has been sent to your email.
                                </p>
                                <button
                                    onClick={handleClose}
                                    className="bg-orange-500 hover:bg-orange-400 text-white font-bold px-6 py-2 rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            /* Form state */
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-white font-bold text-lg">Leave Feedback</h2>
                                    <button
                                        onClick={handleClose}
                                        className="text-gray-500 hover:text-white transition-colors text-xl leading-none"
                                    >
                                        ×
                                    </button>
                                </div>

                                <p className="text-gray-400 text-sm mb-4">
                                    Found a bug? Have a suggestion? We'd love to hear it.
                                </p>

                                {/* Rating */}
                                <div className="mb-4">
                                    <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">
                                        Rating (optional)
                                    </label>
                                    <StarRating value={rating} onChange={setRating} />
                                </div>

                                {/* Message */}
                                <div className="mb-4">
                                    <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">
                                        Message <span className="text-red-400">*</span>
                                    </label>
                                    <textarea
                                        value={message}
                                        onChange={e => setMessage(e.target.value)}
                                        placeholder="Tell us what's on your mind..."
                                        rows={4}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500 resize-none"
                                    />
                                </div>

                                {error && (
                                    <p className="text-red-400 text-sm mb-3">{error}</p>
                                )}

                                <div className="flex gap-3 justify-end">
                                    <button
                                        onClick={handleClose}
                                        className="text-sm text-gray-400 hover:text-white transition-colors px-4 py-2"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={saving}
                                        className="bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-bold px-6 py-2 rounded-lg text-sm transition-colors"
                                    >
                                        {saving ? 'Sending…' : 'Submit'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default FeedbackButton;
